import { Type, type Static } from '@sinclair/typebox';
import { StringEnum, TBuffer } from '../../utils/typebox';

export const FileUploadSchema = Type.Object(
  {
    content: Type.String({
      description:
        'Content of the file encoded as base64. For large files, prefer using multipart, learn more here: https://developer.unipile.com/v2.0/reference/api-usage#sending-files',
    }),
    content_type: Type.String({
      description: `<a href="https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types">MIME type</a> of the file.`,
    }),
    filename: Type.String({ description: 'Name of the file (including extension).' }),
    metadata: Type.Optional(
      Type.Object(
        { duration: Type.Optional(Type.Number({ description: 'Duration of the media file.' })) },
        { description: 'Metadata of the the file.' },
      ),
    ),
  },
  { description: 'A file to be uploaded.', additionalProperties: false },
);

export const FileUploadWithBufferSchema = Type.Composite([FileUploadSchema, Type.Object({ buffer: TBuffer })]);

export const EmailFileUploadSchema = Type.Composite(
  [
    FileUploadSchema,
    Type.Object({
      content_id: Type.Optional(
        Type.String({
          description:
            'The Content-ID (CID) to give to the file, without the surrounding angle brackets. When set, the file is sent as an inline attachment and can be referenced from the HTML content with `cid:<content_id>`, for example `<img src="cid:my-logo">`.',
        }),
      ),
    }),
  ],
  { title: 'Email File', additionalProperties: false },
);

export const MessageFileUploadSchema = Type.Composite(
  [
    FileUploadSchema,
    Type.Object({
      send_mode: Type.Optional(
        StringEnum(['file', 'native'], {
          description: `Defines how the attachment should be sent, if the provider supports multiple modes.
            Omitted: use the provider’s native default behavior.
            - \`file\` send as a regular uploaded file.
            - \`native\` send as a native media (displayed picture, voice note, recorded video, etc.)`,
          default: 'native',
        }),
      ),
      voice_note: Type.Optional(
        Type.Boolean({
          description: 'Deprecated compatibility flag for audio attachments. Use `send_mode: "native"` instead.',
          deprecated: true,
        }),
      ),
    }),
  ],
  { title: 'Message File', additionalProperties: false },
);

export const MessageFileUploadWithBufferSchema = Type.Composite([MessageFileUploadSchema, Type.Object({ buffer: TBuffer })]);

export type FileUpload = Static<typeof FileUploadSchema>;
export type FileUploadWithBuffer = Static<typeof FileUploadWithBufferSchema>;
export type EmailFileUpload = Static<typeof EmailFileUploadSchema>;
export type MessageFileUpload = Static<typeof MessageFileUploadSchema>;
export type MessageFileUploadWithBuffer = Static<typeof MessageFileUploadWithBufferSchema>;
